/**
 **@author       yontaek@zumper.com (Yon Taek Chung)
 **@copyright    Copyright (c) 2024, Zumper
 */

import { Box, Flex, Text } from '@chakra-ui/react'
import { PageContentCarousel } from '@zcode-ui/web-zumper-ui'
import { useGeoLocation } from '@zcode/nr/es/shared/components/GeoLocation'
import { selectCityName } from '@zcode/nr/es/shared/components/GeoLocation/selectors'
import { toTitleCase } from '@zcode/nr/es/shared/utils'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectIsSpotlightListablesLoading,
  selectSpotlightListables,
} from '../../../modules/homepage/selectors'
import { STRListItemContainer } from '../../main/Rail/Listables/STRListItem'

const HomepageSpotlightListings = () => {
  const [currentPage, setCurrentPage] = useState(0)
  const spotlightListings = useSelector(selectSpotlightListables)
  const isLoading = useSelector(selectIsSpotlightListablesLoading)
  const [getGeoLocation, isSafeForClientOnly] = useGeoLocation()
  const geoLocation = isSafeForClientOnly ? getGeoLocation() : {}
  const city = selectCityName(geoLocation)
  const matching = spotlightListings?.length ?? 0

  if (!isLoading && !matching) {
    return null
  }

  const calculateTranslateX = () => {
    return `translateX(-${currentPage * 100}%)`
  }

  return spotlightListings?.length ? (
    <Flex flexDir="column" overflow="hidden">
      <Flex alignItems="center" justifyContent="space-between" mb={{ base: 6, desktop: 8 }}>
        <Text
          fontSize={{ base: '28px', desktop: '32px' }}
          fontWeight="bold"
          fontFamily="heading"
          lineHeight="120%"
          letterSpacing={{ base: '-0.28px', desktop: '-0.32px' }}
        >
          {matching > 1 ? `${matching} must-see properties` : 'Must-see property'} near{' '}
          {toTitleCase(city)}
        </Text>
        {spotlightListings.length > 1 && (
          <Box<'div'> display={{ base: 'none', lg: 'block' }}>
            <PageContentCarousel
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={spotlightListings.length}
            />
          </Box>
        )}
      </Flex>
      <Box<'div'>
        display={{ base: 'none', desktop: 'block' }}
        borderRadius="24px"
        overflow="hidden"
        width="100%"
      >
        <Flex transform={calculateTranslateX()} w="100%" transition="transform 0.5s ease-in-out">
          {spotlightListings.map((listing, idx) => (
            <Box<'div'> key={`spotlight-${idx}`} flexShrink={0} w="100%">
              <STRListItemContainer
                analyticChannel="List"
                entity={listing}
                forceSpotlightListingCardLarge
                index={idx}
                origin="homepage"
              />
            </Box>
          ))}
        </Flex>
      </Box>
      <Box<'div'>
        display={{ base: 'block', desktop: 'none' }}
        borderRadius="8px"
        overflow="hidden"
        width="100%"
      >
        <Flex>
          <STRListItemContainer
            analyticChannel="List"
            entity={spotlightListings[currentPage]}
            forceSpotlightListingCard
            index={currentPage}
            origin="homepage"
          />
        </Flex>
      </Box>
      {spotlightListings.length > 1 && (
        <Box<'div'> display={{ base: 'block', lg: 'none' }} mt={6}>
          <PageContentCarousel
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={spotlightListings.length}
          />
        </Box>
      )}
    </Flex>
  ) : null
}

export default HomepageSpotlightListings
